import { Controller } from '@hotwired/stimulus';

// Diacritic-insensitive fold: lowercase + NFD-decompose + strip combining marks
// (U+0300–U+036F) so "belair" matches "Bélair". Known NFD limit: ligatures like
// œ/æ do not decompose, so "oeuf" won't match "œuf".
const fold = (s) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * ProductUpdatesFilterController — filtre client de la page « Nouveautés »
 * (/nouveautes) : catégorie (pastilles) + recherche texte.
 *
 * Targets:
 *   - query  : champ de recherche
 *   - item   : wrapper d'une carte (`data-category`, `data-search-text`)
 *   - group  : <section> d'un mois (masquée si plus aucune carte visible)
 *   - empty  : bloc « Aucune nouveauté ne correspond » (attribut `hidden`)
 *   - status : région aria-live annonçant le nombre de résultats
 *
 * Actions:
 *   - filter : écoute `filter-pills:change` (event.detail.value)
 *   - search : `input` du champ de recherche
 *   - clear  : vide la recherche, revient à la pastille « Tout » (clic
 *              programmatique : filter-pills met aria-selected à jour et
 *              renvoie filter-pills:change), réapplique et redonne le focus
 *              au champ
 *
 * Le texte cherché vient de `data-search-text` (titre, résumé et détail, marques
 * Markdown retirées : ProductUpdatesHelper#product_update_search_text),
 * lu par `dataset` — jamais par innerHTML. Les compteurs `[data-count-for]`
 * des pastilles reflètent la recherche seule, toutes catégories confondues.
 *
 * @example
 *   <div data-controller="product-updates-filter"
 *        data-action="filter-pills:change->product-updates-filter#filter">
 *     <input data-product-updates-filter-target="query"
 *            data-action="input->product-updates-filter#search">
 *     <span class="sr-only" aria-live="polite" data-product-updates-filter-target="status"></span>
 *     <section data-product-updates-filter-target="group">
 *       <div data-product-updates-filter-target="item" data-category="feature" data-search-text="…"></div>
 *     </section>
 *     <div data-product-updates-filter-target="empty" hidden></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ['query', 'item', 'group', 'empty', 'status'];

  connect() {
    this.category = 'all';
  }

  filter(event) {
    this.category = event.detail?.value || 'all';
    this.apply();
  }

  search() {
    this.apply();
  }

  clear() {
    if (this.hasQueryTarget) this.queryTarget.value = '';
    const allPill = this.element.querySelector('[role="tab"][data-value="all"]');
    if (allPill && allPill.getAttribute('aria-selected') !== 'true') allPill.click();
    this.category = 'all';
    this.apply();
    if (this.hasQueryTarget) this.queryTarget.focus();
  }

  apply() {
    const needle = this.hasQueryTarget ? fold(this.queryTarget.value.trim()) : '';
    const counts = { all: 0 };
    let visible = 0;

    this.itemTargets.forEach((item) => {
      const category = item.dataset.category;
      const matchesSearch = needle === '' || fold(item.dataset.searchText || '').includes(needle);
      if (matchesSearch) {
        counts.all += 1;
        counts[category] = (counts[category] || 0) + 1;
      }
      const shown = matchesSearch && (this.category === 'all' || category === this.category);
      item.hidden = !shown;
      if (shown) visible += 1;
    });

    this.groupTargets.forEach((group) => {
      group.hidden = !this.itemTargets.some((item) => group.contains(item) && !item.hidden);
    });

    this.element.querySelectorAll('[data-count-for]').forEach((span) => {
      span.textContent = String(counts[span.dataset.countFor] || 0);
    });

    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible > 0;
    if (this.hasStatusTarget) this.statusTarget.textContent = this.statusLabel(visible);
  }

  statusLabel(count) {
    if (count === 0) return 'Aucune nouveauté trouvée';
    return count === 1 ? '1 nouveauté' : `${count} nouveautés`;
  }
}
